import { useMemo } from 'react';

import { REPORT_NAME_KEYS } from '@/utils/constants';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useGetBeraReportV2, useGetBeraStepKeyOptionList, useGetBeraStepKeyResultList } from '@/hooks';
import { useBeraSteps, useCustomReport } from '@/components/views/BeraJob/hooks/hooks';
import type { FileForCustomReport } from '@/components/ui/Inputs/types/response';
import type { BeraJobSummary, BeraReportV2, BeraReportV2Result } from '@/types/Bera';

import { buildInformations, buildResultSteps, buildScores, isEvaluationStep } from '../utils';

const EMPTY_TEXT = '';

interface ReportV2ResultParams {
	report: BeraReportV2;
	file: FileForCustomReport;
	jobSummary: BeraJobSummary;
}

export function useReportV2Result({ report, file, jobSummary }: ReportV2ResultParams) {
	const { organization, company } = useApplicationContext();

	const tenant = { organization_id: organization?.id, company_id: company?.id };

	const details = useGetBeraReportV2({ ...tenant, id: report.id });

	const { data: customReport } = useCustomReport({ ...tenant, reportName: REPORT_NAME_KEYS.BERA });

	const customReportId = file.CustomReportsFiles?.custom_report_id ?? customReport?.id;

	const stepsList = useBeraSteps({ ...tenant, custom_report_id: customReportId });

	const stepKeyReferences = useMemo(() => {
		const evaluationSteps = stepsList.data.filter(isEvaluationStep);
		const references = evaluationSteps.flatMap((step) =>
			step.step_key.map((stepKey) => ({ step_id: step.id, step_key_id: stepKey.id }))
		);

		return references;
	}, [stepsList.data]);

	const stepKeyIds = useMemo(() => stepKeyReferences.map((reference) => reference.step_key_id), [stepKeyReferences]);

	const stepKeyResultParams = { ...tenant, bera_report_id: report.id, step_keys: stepKeyReferences };

	const stepKeyResults = useGetBeraStepKeyResultList(stepKeyResultParams);

	const options = useGetBeraStepKeyOptionList({ ...tenant, step_key_ids: stepKeyIds });

	const isLoading = details.isLoading || stepsList.isLoading || stepKeyResults.isLoading || options.isLoading;

	const isError = details.isError || stepsList.isError || stepKeyResults.isError || options.isError;

	const result = useMemo(() => {
		if (!details.data || !stepKeyResults.data || !options.data) {
			return undefined;
		}

		const steps = buildResultSteps({
			steps: stepsList.data,
			stepKeyResults: stepKeyResults.data,
			options: options.data
		});
		const scores = buildScores(steps, !!details.data.has_known_injury);
		const informationsParams = {
			report,
			details: details.data,
			jobSummary,
			organizationName: organization?.name ?? EMPTY_TEXT,
			companyName: company?.name ?? EMPTY_TEXT,
			fileName: file.original_name
		};
		const informations = buildInformations(informationsParams);

		const value: BeraReportV2Result = {
			id: report.id,
			informations,
			scores,
			steps,
			comment: details.data.comment ?? EMPTY_TEXT,
			consolidated: details.data.consolidated
		};

		return value;
	}, [
		details.data,
		stepKeyResults.data,
		options.data,
		stepsList.data,
		report,
		jobSummary,
		organization,
		company,
		file
	]);

	function refetch(): void {
		details.refetch();
		stepsList.refetch();
		stepKeyResults.refetch();
		options.refetch();
	}

	return { result, isLoading, isError, refetch };
}
